/**
 * CompetitorInsights — Opportunities card for the Dashboard
 *
 * New file → frontend/src/components/CompetitorInsights.js
 *
 * Usage in DashboardPage.js:
 *   import CompetitorInsights from '../components/CompetitorInsights';
 *   // Place wherever you want it in the JSX:
 *   <CompetitorInsights />
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

export default function CompetitorInsights() {
  const navigate = useNavigate();
  const [insights, setInsights] = useState([]);
  const [competitorCount, setCompetitorCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const fetchInsights = useCallback(async () => {
    try {
      const data = await api.get('/competitors/insights');
      setInsights(data.insights || []);
      setCompetitorCount(data.competitor_count || 0);

      // Auto-trigger extraction if competitors exist but no insights yet
      if ((data.competitor_count || 0) > 0 && (!data.insights || data.insights.length === 0)) {
        setRefreshing(true);
        try {
          await api.post('/competitors/insights/refresh', {});
          const refreshed = await api.get('/competitors/insights');
          setInsights(refreshed.insights || []);
        } catch (err) {
          if (err?.response?.status === 429) {
            setError('Rate limit reached — try again in a few minutes');
          }
        } finally {
          setRefreshing(false);
        }
      }
    } catch {
      // Silently fail — 403 means not subscribed, just hide the card
      setInsights([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchInsights(); }, [fetchInsights]);

  const handleRefresh = async () => {
    setRefreshing(true);
    setError('');
    try {
      const res = await api.post('/competitors/insights/refresh', {});
      if (res.insights) {
        await fetchInsights();
      }
    } catch (err) {
      const msg = err?.response?.status === 429
        ? 'Rate limit reached — try again in a few minutes'
        : err.message || 'Refresh failed';
      setError(msg);
    } finally {
      setRefreshing(false);
    }
  };

  const handleDismiss = async (insightId) => {
    try {
      await api.post(`/competitors/insights/${insightId}/dismiss`, {});
      setInsights(prev => prev.filter(i => i.id !== insightId));
    } catch {
      // Silently fail
    }
  };

  if (loading) return null;
  if (competitorCount === 0) return null;

  const activeInsights = insights.filter(i => !i.dismissed);

  if (activeInsights.length === 0) {
    return (
      <div className="card" style={{ padding: '20px 24px', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <span style={{ fontSize: 18 }}>💡</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>Competitor Opportunities</span>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-light)', lineHeight: 1.5, margin: '0 0 12px' }}>
          No insights yet. Sync your competitors' reviews first, then click below to discover their weaknesses.
        </p>
        <button
          className="btn btn-ghost"
          onClick={handleRefresh}
          disabled={refreshing}
          style={{ fontSize: 12 }}
        >
          {refreshing ? 'Analysing...' : 'Analyse Competitors'}
        </button>
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: '20px 24px', marginBottom: 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 18 }}>💡</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>Competitor Opportunities</span>
          <span style={{
            fontSize: 11, fontWeight: 700, color: 'var(--green)',
            background: 'rgba(22,163,74,0.08)', padding: '2px 8px', borderRadius: 10,
          }}>
            {activeInsights.length}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            style={{
              background: 'none', border: 'none', color: 'var(--text-light)',
              fontSize: 12, fontWeight: 600, cursor: 'pointer',
              fontFamily: "'DM Sans', system-ui, sans-serif",
              opacity: refreshing ? 0.5 : 1,
            }}
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
          <button
            onClick={() => navigate('/competitors')}
            style={{
              background: 'none', border: 'none', color: 'var(--green)',
              fontSize: 12, fontWeight: 600, cursor: 'pointer',
              fontFamily: "'DM Sans', system-ui, sans-serif",
            }}
          >
            View all
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          fontSize: 12, color: '#dc2626', marginBottom: 12,
          padding: '8px 12px', background: 'rgba(220,38,38,0.06)', borderRadius: 6,
        }}>
          {error}
        </div>
      )}

      {/* Insight items (show top 3) */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {activeInsights.slice(0, 3).map((insight) => {
          const dotColor = insight.mention_count >= 4 ? '#dc2626'
            : insight.mention_count >= 2 ? '#f59e0b' : '#6b7280';

          return (
            <div key={insight.id} style={{
              display: 'flex', alignItems: 'flex-start', gap: 12,
              padding: '12px 14px', borderRadius: 8,
              background: 'var(--bg)', border: '1px solid var(--border)',
            }}>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: 13, fontWeight: 700, color: 'var(--text)',
                  display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4,
                  textTransform: 'capitalize',
                }}>
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: dotColor, flexShrink: 0,
                  }} />
                  {insight.theme}
                  <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-light)', marginLeft: 4 }}>
                    {insight.mention_count} mention{insight.mention_count !== 1 ? 's' : ''}
                  </span>
                </div>
                <p style={{ fontSize: 12.5, color: 'var(--text)', lineHeight: 1.5, margin: '4px 0' }}>
                  {insight.opportunity_text}
                </p>
                <p style={{ fontSize: 11, color: 'var(--text-light)', margin: 0 }}>
                  Affected: {insight.competitor_names}
                </p>
              </div>
              <button
                onClick={() => handleDismiss(insight.id)}
                title="Dismiss this insight"
                style={{
                  background: 'none', border: 'none', color: 'var(--border)',
                  fontSize: 14, cursor: 'pointer', padding: '2px 4px',
                  flexShrink: 0, lineHeight: 1,
                }}
              >
                ✕
              </button>
            </div>
          );
        })}
      </div>

      <p style={{ fontSize: 11, color: 'var(--text-light)', marginTop: 14, marginBottom: 0, textAlign: 'center' }}>
        Based on {competitorCount} tracked competitor{competitorCount !== 1 ? 's' : ''} · Refreshes weekly
      </p>
    </div>
  );
}
